import { onDocumentReady } from '@theme/utilities';

const ORIGINAL_CENTS_ATTR = 'data-site-wide-discount-original-cents';
const PROCESSED_ATTR = 'data-site-wide-discount-processed';
const ONETIME_STRIKE_ATTR = 'data-site-wide-discount-onetime-strike';
const SALE_PRICE_COLOR = 'var(--color-brand-red)';
const POLL_INTERVAL_MS = 500;
const POLL_DURATION_MS = 30000;
const APPLY_DEBOUNCE_MS = 100;

const DEFAULT_CURRENCY_DECIMALS = 2;
const CURRENCY_DECIMALS = {
  BHD: 3,
  BIF: 0,
  CLP: 0,
  DJF: 0,
  GNF: 0,
  ISK: 0,
  JOD: 3,
  JPY: 0,
  KMF: 0,
  KRW: 0,
  KWD: 3,
  LYD: 3,
  OMR: 3,
  PYG: 0,
  RWF: 0,
  TND: 3,
  UGX: 0,
  VND: 0,
  VUV: 0,
  XAF: 0,
  XOF: 0,
  XPF: 0,
};

/** @type {WeakMap<Element, Record<string, number>>} */
const originalCentsByContainer = new WeakMap();

/**
 * @param {string} text
 * @returns {number | null}
 */
function parsePriceToCents(text) {
  const trimmed = text.trim();
  if (!trimmed) return null;

  const numeric = trimmed.replace(/[^\d.,]/g, '');
  if (!numeric) return null;

  const lastComma = numeric.lastIndexOf(',');
  const lastDot = numeric.lastIndexOf('.');

  let normalized;
  if (lastComma > lastDot) {
    normalized = numeric.replace(/\./g, '').replace(',', '.');
  } else {
    normalized = numeric.replace(/,/g, '');
  }

  const value = Number.parseFloat(normalized);
  if (Number.isNaN(value)) return null;

  return Math.round(value * 100);
}

/**
 * @param {number} cents
 * @param {string} thousandsSeparator
 * @param {string} decimalSeparator
 * @param {number} precision
 * @returns {string}
 */
function formatCents(cents, thousandsSeparator, decimalSeparator, precision) {
  const roundedNumber = (cents / 100).toFixed(precision);
  let [whole = '0', fraction = ''] = roundedNumber.split('.');

  whole = whole.replace(/\d(?=(\d\d\d)+(?!\d))/g, (digit) => digit + thousandsSeparator);

  return precision <= 0 ? whole : whole + decimalSeparator + fraction.padEnd(precision, '0');
}

/**
 * @param {number} cents
 * @param {string} moneyFormat
 * @param {string} currency
 * @returns {string}
 */
function formatMoneyFromCents(cents, moneyFormat, currency) {
  const template = moneyFormat || '${{amount}}';
  const precision = CURRENCY_DECIMALS[currency.toUpperCase()] ?? DEFAULT_CURRENCY_DECIMALS;

  return template.replace(/{{\s*(\w+)\s*}}/g, (_, placeholder) => {
    if (typeof placeholder !== 'string') return '';

    if (placeholder === 'currency') return currency;

    let thousandsSeparator = ',';
    let decimalSeparator = '.';
    let formatPrecision = precision;

    if (placeholder === 'amount_no_decimals') {
      formatPrecision = 0;
    } else if (placeholder === 'amount_with_comma_separator') {
      thousandsSeparator = '.';
      decimalSeparator = ',';
    } else if (placeholder === 'amount_no_decimals_with_comma_separator') {
      thousandsSeparator = '.';
      formatPrecision = 0;
    } else if (placeholder === 'amount_no_decimals_with_space_separator') {
      thousandsSeparator = ' ';
      formatPrecision = 0;
    } else if (placeholder === 'amount_with_space_separator') {
      thousandsSeparator = ' ';
      decimalSeparator = ',';
    } else if (placeholder === 'amount_with_period_and_space_separator') {
      thousandsSeparator = ' ';
      decimalSeparator = '.';
    } else if (placeholder === 'amount_with_apostrophe_separator') {
      thousandsSeparator = "'";
      decimalSeparator = '.';
    }

    return formatCents(cents, thousandsSeparator, decimalSeparator, formatPrecision);
  });
}

/**
 * @param {Element} element
 * @returns {string}
 */
function getVisiblePriceText(element) {
  const clone = /** @type {Element} */ (element.cloneNode(true));
  clone.querySelectorAll('.visually-hidden').forEach((node) => node.remove());
  return clone.textContent?.trim() ?? '';
}

/**
 * @param {Element} element
 * @param {string} formattedPrice
 */
function updatePriceElementText(element, formattedPrice) {
  for (const node of [...element.childNodes].reverse()) {
    if (node.nodeType === Node.TEXT_NODE && node.textContent?.trim()) {
      node.textContent = formattedPrice;
      return;
    }
  }

  element.append(document.createTextNode(formattedPrice));
}

/**
 * @param {Element} priceEl
 * @param {Element} strikeEl
 */
function ensurePriceBeforeStrike(priceEl, strikeEl) {
  if (strikeEl.compareDocumentPosition(priceEl) & Node.DOCUMENT_POSITION_FOLLOWING) {
    priceEl.after(strikeEl);
  }
}

/**
 * @param {Element} element
 */
function styleDiscountedPriceElement(element) {
  if (element instanceof HTMLElement) {
    element.style.setProperty('color', SALE_PRICE_COLOR, 'important');
    element.style.setProperty('font-weight', '700', 'important');
  }
}

/**
 * @param {Document | ShadowRoot | Element} root
 * @param {string} selector
 * @returns {Element[]}
 */
function queryAllDeep(root, selector) {
  /** @type {Element[]} */
  const results = [];

  /** @param {Document | ShadowRoot | Element} node */
  const walk = (node) => {
    if (!node.querySelectorAll) return;

    node.querySelectorAll(selector).forEach((element) => results.push(element));
    node.querySelectorAll('*').forEach((element) => {
      if (element.shadowRoot) walk(element.shadowRoot);
    });
  };

  walk(root);
  return results;
}

/**
 * @param {Element | null} element
 * @param {string} selector
 * @returns {Element | null}
 */
function closestDeep(element, selector) {
  let current = element;

  while (current) {
    if (current instanceof Element && current.matches(selector)) return current;

    if (current.parentElement) {
      current = current.parentElement;
      continue;
    }

    const root = current.parentNode;
    if (root instanceof ShadowRoot) {
      current = root.host;
      continue;
    }

    break;
  }

  return null;
}

/**
 * @param {Element} element
 * @returns {string | null}
 */
function getProductIdFromElement(element) {
  const host = closestDeep(element, '[data-product-id]');
  if (host) return host.getAttribute('data-product-id');

  // Recharge (and similar widgets) may sit outside product-scoped wrappers on PDPs.
  const templateName = typeof Theme !== 'undefined' ? String(Theme.template?.name || '') : '';
  if (!templateName.startsWith('product')) return null;

  const pageProduct =
    document.querySelector('sticky-add-to-cart[data-product-id]') ??
    document.querySelector('product-form-component[data-product-id]') ??
    document.querySelector('variant-picker[data-product-id]');

  return pageProduct?.getAttribute('data-product-id') ?? null;
}

/**
 * @param {Document | ShadowRoot | Element} root
 * @returns {Element[]}
 */
function findRechargePriceContainers(root) {
  const selectors = [
    '.rc-purchase-option__prices',
    '[part="rc-purchase-option__prices"]',
    '[part*="rc-purchase-option__prices"]',
  ];

  /** @type {Element[]} */
  const containers = [];

  selectors.forEach((selector) => {
    queryAllDeep(root, selector).forEach((container) => {
      if (!containers.includes(container)) containers.push(container);
    });
  });

  return containers;
}

/**
 * @param {Element} container
 * @returns {'subscription' | 'onetime' | null}
 */
function getRechargePriceType(container) {
  if (
    closestDeep(
      container,
      '.rc-purchase-option__subscription, [part*="rc-purchase-option__subscription"]'
    )
  ) {
    return 'subscription';
  }

  if (
    closestDeep(
      container,
      '.rc-purchase-option__onetime, .rc-selection__onetime, [part*="rc-purchase-option__onetime"]'
    )
  ) {
    return 'onetime';
  }

  if (container.querySelector('[part="rc-purchase-option__discounted-price"], .rc-price.strike-through')) {
    return 'subscription';
  }

  if (container.querySelector('[part="rc-purchase-option__price"]')) {
    return 'onetime';
  }

  return null;
}

class SiteWideDiscount {
  /** @type {number} */
  #discountPercent;

  /** @type {string} */
  #moneyFormat;

  /** @type {string} */
  #currency;

  /** @type {Set<string>} */
  #includeProductIds;

  /** @type {MutationObserver | null} */
  #observer = null;

  /** @type {number | null} */
  #debounceTimer = null;

  /** @type {number | null} */
  #pollTimer = null;

  /** @type {number} */
  #pollStartedAt = 0;

  /** @type {boolean} */
  #isApplying = false;

  /**
   * @param {{
   *   discountPercent: number;
   *   moneyFormat: string;
   *   currency: string;
   *   includeProductIds?: Array<number | string>;
   * }} config
   */
  constructor(config) {
    this.#discountPercent = Number(config.discountPercent) || 0;
    this.#moneyFormat = config.moneyFormat;
    this.#currency = config.currency;
    this.#includeProductIds = new Set(
      (config.includeProductIds ?? []).map((id) => String(id)).filter(Boolean)
    );
  }

  /**
   * When the include list is empty, discount applies to all products.
   * @param {string | null} productId
   * @returns {boolean}
   */
  #isProductIncluded(productId) {
    if (this.#includeProductIds.size === 0) return true;
    if (productId == null) return false;
    return this.#includeProductIds.has(String(productId));
  }

  init() {
    if (this.#discountPercent <= 0) return;

    document.body.dataset.siteWideDiscount = String(this.#discountPercent);
    this.#apply();
    this.#startPolling();

    this.#observer = new MutationObserver(() => this.#scheduleApply());
    this.#observer.observe(document.documentElement, { childList: true, subtree: true });

    document.addEventListener('variant:update', () => this.#handleVariantUpdate());
    window.addEventListener('load', () => this.#scheduleApply());
  }

  #handleVariantUpdate() {
    this.#clearAllCaches();
    this.#apply();
    window.setTimeout(() => this.#apply(), 300);
    window.setTimeout(() => this.#apply(), 800);
  }

  #clearAllCaches() {
    document.querySelectorAll(`[${PROCESSED_ATTR}]`).forEach((element) => {
      if (!(element instanceof Element)) return;
      if (element.closest('.cross__sell--product')) return;

      originalCentsByContainer.delete(element);
      element.removeAttribute(PROCESSED_ATTR);
      element.querySelectorAll(`[${ORIGINAL_CENTS_ATTR}]`).forEach((child) => {
        child.removeAttribute(ORIGINAL_CENTS_ATTR);
      });
    });
  }

  #startPolling() {
    this.#pollStartedAt = Date.now();

    this.#pollTimer = window.setInterval(() => {
      this.#apply();

      if (Date.now() - this.#pollStartedAt >= POLL_DURATION_MS && this.#pollTimer) {
        window.clearInterval(this.#pollTimer);
        this.#pollTimer = null;
      }
    }, POLL_INTERVAL_MS);
  }

  #scheduleApply() {
    if (this.#debounceTimer) window.clearTimeout(this.#debounceTimer);

    this.#debounceTimer = window.setTimeout(() => {
      this.#debounceTimer = null;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => this.#apply());
      });
    }, APPLY_DEBOUNCE_MS);
  }

  #apply() {
    if (this.#isApplying) return;

    this.#isApplying = true;
    this.#observer?.disconnect();

    try {
      this.#applyThemePrices();
      this.#applyRechargePrices();
    } finally {
      this.#isApplying = false;
      this.#observer?.observe(document.documentElement, { childList: true, subtree: true });
    }
  }

  /**
   * @param {Element} container
   * @param {string} key
   * @param {Element} element
   * @param {'theme' | 'recharge'} source
   * @returns {number | null}
   */
  #readOriginalCents(container, key, element, source = 'theme') {
    const currentCents = parsePriceToCents(getVisiblePriceText(element));
    const storedOnContainer = originalCentsByContainer.get(container)?.[key];

    if (source === 'theme') {
      const compareAt = container.querySelector('.compare-at-price');
      if (compareAt instanceof Element) {
        const compareCents = parsePriceToCents(getVisiblePriceText(compareAt));
        if (compareCents != null) {
          this.#storeOriginalCents(container, key, compareCents);
          element.setAttribute(ORIGINAL_CENTS_ATTR, String(compareCents));
          return compareCents;
        }
      }
    }

    if (storedOnContainer != null && currentCents != null) {
      const expectedDiscounted = this.#discountedCents(storedOnContainer);

      if (currentCents === expectedDiscounted || currentCents === storedOnContainer) {
        return storedOnContainer;
      }

      if (source === 'recharge') {
        this.#storeOriginalCents(container, key, currentCents);
        element.setAttribute(ORIGINAL_CENTS_ATTR, String(currentCents));
        return currentCents;
      }
    }

    if (storedOnContainer != null) return storedOnContainer;

    const storedOnElement = element.getAttribute(ORIGINAL_CENTS_ATTR);
    if (storedOnElement) {
      const parsed = Number.parseInt(storedOnElement, 10);
      if (!Number.isNaN(parsed)) {
        this.#storeOriginalCents(container, key, parsed);
        return parsed;
      }
    }

    if (currentCents == null) return null;

    element.setAttribute(ORIGINAL_CENTS_ATTR, String(currentCents));
    this.#storeOriginalCents(container, key, currentCents);
    return currentCents;
  }

  /**
   * Recharge keeps subscription strikethrough in sync on variant change, but the
   * discounted price element can remain stale after we overwrite it. Derive the
   * native subscription price from strikethrough (10% subscription discount).
   * @param {Element} pricesContainer
   * @param {Element} discounted
   * @returns {number | null}
   */
  #readRechargeSubscriptionOriginalCents(pricesContainer, discounted) {
    const strikethroughCents = this.#getRechargeFullPriceCents(pricesContainer);
    if (strikethroughCents == null) return null;

    const expectedNativeSubCents = Math.round(strikethroughCents * 0.9);
    const currentCents = parsePriceToCents(getVisiblePriceText(discounted));
    const expectedDiscounted = this.#discountedCents(expectedNativeSubCents);

    if (
      currentCents != null &&
      (currentCents === expectedNativeSubCents ||
        currentCents === expectedDiscounted ||
        Math.abs(currentCents - expectedNativeSubCents) <= 1 ||
        Math.abs(currentCents - expectedDiscounted) <= 1)
    ) {
      this.#storeOriginalCents(pricesContainer, 'subscription-discounted', expectedNativeSubCents);
      this.#storeOriginalCents(pricesContainer, 'subscription-strikethrough', strikethroughCents);
      return expectedNativeSubCents;
    }

    this.#storeOriginalCents(pricesContainer, 'subscription-discounted', expectedNativeSubCents);
    this.#storeOriginalCents(pricesContainer, 'subscription-strikethrough', strikethroughCents);
    discounted.setAttribute(ORIGINAL_CENTS_ATTR, String(expectedNativeSubCents));
    return expectedNativeSubCents;
  }

  /**
   * Recharge keeps the subscription strikethrough in sync with the selected variant.
   * @param {Element} pricesContainer
   * @returns {number | null}
   */
  #getRechargeFullPriceCents(pricesContainer) {
    const widgetRoot =
      closestDeep(pricesContainer, '.rc-content-wrap, [part*="rc-content-wrap"], .rc-selection__root') ??
      pricesContainer;

    const subscriptionOriginal = queryAllDeep(
      widgetRoot,
      '.rc-purchase-option__subscription [part="rc-purchase-option__original-price"], .rc-purchase-option__subscription .rc-price.strike-through'
    )[0];

    if (!(subscriptionOriginal instanceof Element)) return null;

    return parsePriceToCents(getVisiblePriceText(subscriptionOriginal));
  }

  /**
   * @param {Element} container
   * @param {string} key
   * @param {number} cents
   */
  #storeOriginalCents(container, key, cents) {
    const existing = originalCentsByContainer.get(container) ?? {};
    existing[key] = cents;
    originalCentsByContainer.set(container, existing);
  }

  /**
   * @param {number} originalCents
   * @returns {number}
   */
  #discountedCents(originalCents) {
    return Math.round((originalCents * (100 - this.#discountPercent)) / 100);
  }

  /**
   * @param {number} cents
   * @returns {string}
   */
  #format(cents) {
    return formatMoneyFromCents(cents, this.#moneyFormat, this.#currency);
  }

  #applyThemePrices() {
    document
      .querySelectorAll(
        'product-price [ref="priceContainer"], product-price .priceContainer, .sticky-add-to-cart__price .priceContainer, .sticky-add-to-cart__drawer-price .priceContainer, .cross__sell--product-price .priceContainer'
      )
      .forEach((container) => {
        if (!this.#isProductIncluded(getProductIdFromElement(container))) return;

        const salePrice = container.querySelector('.price:not(.price-range)');
        if (!(salePrice instanceof Element)) return;

        const originalCents = this.#readOriginalCents(container, 'theme-sale', salePrice);
        if (originalCents == null) return;

        let compareAt = container.querySelector('.compare-at-price');
        if (!(compareAt instanceof Element)) {
          compareAt = document.createElement('span');
          compareAt.className = 'compare-at-price';
          compareAt.textContent = this.#format(originalCents);
          salePrice.after(compareAt);
        } else {
          compareAt.textContent = this.#format(originalCents);
          ensurePriceBeforeStrike(salePrice, compareAt);
        }

        const discounted = this.#format(this.#discountedCents(originalCents));
        if (salePrice.textContent?.trim() !== discounted) {
          salePrice.textContent = discounted;
        }

        styleDiscountedPriceElement(salePrice);

        container.setAttribute(PROCESSED_ATTR, 'true');
      });
  }

  #applyRechargePrices() {
    findRechargePriceContainers(document).forEach((container) => {
      if (!this.#isProductIncluded(getProductIdFromElement(container))) return;

      const type = getRechargePriceType(container);
      if (type === 'subscription') {
        this.#applyRechargeSubscriptionContainer(container);
      } else if (type === 'onetime') {
        this.#applyRechargeOnetimeContainer(container);
      }
    });
  }

  /**
   * @param {Element} pricesContainer
   */
  #applyRechargeSubscriptionContainer(pricesContainer) {
    const discounted =
      pricesContainer.querySelector('[part="rc-purchase-option__discounted-price"]') ??
      pricesContainer.querySelector('.rc-price:not(.strike-through)');

    if (!(discounted instanceof Element)) return;

    const originalCents = this.#readRechargeSubscriptionOriginalCents(pricesContainer, discounted);
    if (originalCents == null) return;

    updatePriceElementText(discounted, this.#format(this.#discountedCents(originalCents)));
    styleDiscountedPriceElement(discounted);

    const strike =
      pricesContainer.querySelector('[part="rc-purchase-option__original-price"]') ??
      pricesContainer.querySelector(`.rc-price.strike-through:not([${ONETIME_STRIKE_ATTR}])`);
    if (strike instanceof Element) {
      ensurePriceBeforeStrike(discounted, strike);
    }

    pricesContainer.setAttribute(PROCESSED_ATTR, 'true');
  }

  /**
   * @param {Element} pricesContainer
   */
  #applyRechargeOnetimeContainer(pricesContainer) {
    const fullPriceCents = this.#getRechargeFullPriceCents(pricesContainer);

    const priceEl =
      pricesContainer.querySelector('[part="rc-purchase-option__discounted-price"]') ??
      pricesContainer.querySelector('[part="rc-purchase-option__price"]') ??
      pricesContainer.querySelector(`.rc-price:not(.strike-through):not([${ONETIME_STRIKE_ATTR}])`);

    if (!(priceEl instanceof Element)) return;

    const originalCents =
      fullPriceCents ??
      this.#readOriginalCents(pricesContainer, 'onetime-original', priceEl, 'recharge');
    if (originalCents == null) return;

    let strikeEl = pricesContainer.querySelector(`[${ONETIME_STRIKE_ATTR}]`);
    if (!(strikeEl instanceof Element)) {
      strikeEl = document.createElement('span');
      strikeEl.className = 'rc-price strike-through text-muted';
      strikeEl.setAttribute(ONETIME_STRIKE_ATTR, 'true');
      priceEl.after(strikeEl);
    } else {
      ensurePriceBeforeStrike(priceEl, strikeEl);
    }

    strikeEl.textContent = this.#format(originalCents);
    updatePriceElementText(priceEl, this.#format(this.#discountedCents(originalCents)));
    styleDiscountedPriceElement(priceEl);
    pricesContainer.setAttribute(PROCESSED_ATTR, 'true');
  }
}

onDocumentReady(() => {
  if (typeof Theme === 'undefined') return;

  const config = Theme.settings?.siteWideDiscount;
  if (!config || Number(config.discountPercent) <= 0) return;

  new SiteWideDiscount(config).init();
});
